<script setup lang="ts" generic="T">
    // @ts-nocheck
    // nocheck pga att vi har urgamla versioner av allt och TS kaosar

    /**
     * Används för att visualisera data som satts via ett formulär (t.ex en opanmälan som skapats
     * via Operationsanmälan och sen visas i Operationsinformation)
     *
     * Sätt klassen "form-section" på en container som ska innehålla en till flera formulärkomponenter. Den containern
     * kan också få någon av dessa klasser för att avgöra hur många kolumner sektionen kan sträcka ut sig över:
     * span-all: Upp till alla (4) kolumner
     * span-three: Upp till 3 kolumner
     * span-two: Upp till 2 kolumner
     *
     * Sätt någon av ovan klasser (span-all/span-three/span-two) på children inom en container med "form-section"
     * för att avgöra hur många kolumner inom sektionen som komponenten ska ta.
     *
     * T.ex:
     * En div med class "form-section span-three" kommer sträcka ut sig över 3 kolumner. En komponent i den
     * div:en med class "span-all" kommer att sträcka ut sig över alla kolumner inom sektionen, vilket är 3. Om div:en
     * hade haft "span-all" så hade child med "span-cell" sträckt sig över 4 kolumner.
     */

    import { CONFIG_PROVIDE_NAME } from "@/store/modules/types/configModuleTypes";

    import { provide, toRefs } from "vue";

    export interface KFormDisplayProps {
        /**
         * Antal kolumner i grid
         */
        columns?: "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12";
        /**
         * Namn på config. Används för att hämta inställningar
         */
        configModuleName?: string;
        /**
         * Namn på config. Används för att hämta inställningar
         */
        configSubModuleName?: string;
        /**
         * Ska tomma värden visas som default i alla KFormFieldDisplay? Visning av tomt värde innebär att "-" skrivs
         * ut som innehåll om det inte finns något värde. False leder till att fält som inte har värden istället kommer
         * att döljas. Om prop på KFormFieldDisplay specifikt sätts overridas det här värdet på den komponenten
         */
        showEmptyValues?: boolean;
        /**
         * Visuella varianter för formuläret. Kan påverka max bredd och annat
         */
        variant?: "standard" | "dialog";
        /** Mängd padding på hela formuläret. formPadding: sections kan användas för att justera ALLT innehåll så att
         * det är anpassat för numrerade KSections
         */
        formPadding?: "default" | "sections";
    }

    const props = withDefaults(defineProps<KFormDisplayProps>(), {
        columns: "4",
        showEmptyValues: false,
        variant: "standard",
        formPadding: "default",
    });

    const { configModuleName, configSubModuleName, showEmptyValues } = toRefs(props);

    /**
     * Gör namn på module och sub module tillgängligt till alla formulärkomponenter inom formuläret.
     * Används för att hämta inställningar från configModule i store
     */
    provide(CONFIG_PROVIDE_NAME, {
        moduleName: configModuleName?.value,
        subModuleName: configSubModuleName?.value,
    });

    provide("showEmptyValues", showEmptyValues.value);
</script>
<template>
    <div :class="['k-form-display', `variant-${variant}`, `columns-${columns}`, `padding-${formPadding}`]">
        <div v-if="$slots.header" class="form-header">
            <slot name="header" />
        </div>
        <div class="form-content-wrapper">
            <slot />
        </div>
        <div v-if="$slots.actions" class="form-actions">
            <slot name="actions" />
        </div>
    </div>
</template>

<style scoped lang="scss">
    @use "@/components/forms/Form/styles/FormContent";
    @import "@/style/container";
    @import "@/components/forms/Form/styles/FormHeader";
    @import "@/components/forms/Form/styles/FormActions";
    @import "@/style/components";
    @import "@/components/forms/Form/styles/mixins";

    .k-form-display {
        max-width: 100%;

        @media print {
            width: 100% !important;
            max-width: 100% !important;

            &.variant-standard,
            &.variant-dialog {
                width: 100% !important;
                max-width: 100% !important;
            }
        }

        &.padding-sections {
            @include padding-sections;
        }

        &.variant-standard {
            width: $content-max-width;

            &.padding-sections {
                width: calc($content-max-width + $k-section-left-padding);
            }
        }

        &.variant-dialog {
            width: $dialog-wide-content-width;

            &.padding-sections {
                width: calc($dialog-wide-content-width + $k-section-left-padding);
            }
        }

        @include header;

        &.variant-standard {
            @include FormContent.columns(standard);
        }

        &.variant-dialog {
            @include FormContent.columns(dialog);
        }

        @include actions;
    }
</style>
